import React, { useState, useRef, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import './VoiceRecordingFlow.css';
import { sanitizeInput } from '../utils/security';
import { auth, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { getUserFolderPath } from '../utils/firebaseUtils';
import TorusAnimation from './TorusAnimation';
import MicToggle from './MicToggle';
import { requestMicrophone, getFriendlyMicError } from '../utils/media';
import TextToReadPopup from './TextToReadPopup';
import aiService from '../services/AIService';
import { analyzeAudioLocally } from '../utils/localAudioAnalysis';
import { useSpacebarRecording } from '../hooks/useSpacebarRecording';

// Configuration VoxTest - 10 étapes avec pauses respiratoires (287 secondes = 4m47s)
const PHASES_WITH_PAUSES = [
  { type: 'record', id: 'etape1', duration: 15, instruction: '"aaaaaa..." (3 fois, calme et long)' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape2', duration: 15, instruction: '"eeeeee..." (3 fois)' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape3', duration: 10, instruction: '"JE SUIS. JE SUIS. JE SUIS."' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape4', duration: 10, instruction: '"Votre prénom et nom. Votre prénom et nom. Votre prénom et nom."' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape5', duration: 45, instruction: '"Aujourd\'hui je fais ce test vocal pour..."' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape6', duration: 45, instruction: 'Texte triste ou joyeux choisi' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape7', duration: 70, instruction: 'Raconte ta journée ou un souvenir' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape8', duration: 15, instruction: '"Un, deux, trois, ... jusqu\'à vingt"' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape9', duration: 15, instruction: 'Inspire / expire fort, puis 2 toux' },
  { type: 'pause', duration: 3, instruction: '✨ Respirez profondément...' },
  { type: 'record', id: 'etape10', duration: 20, instruction: '1 phrase de chanson connue (libre)' }
];

// Calcul du temps total et des étapes pour compatibilité
const TOTAL_DURATION = PHASES_WITH_PAUSES.reduce((sum, phase) => sum + phase.duration, 0); // 287 secondes
const RECORD_PHASES = PHASES_WITH_PAUSES.filter(phase => phase.type === 'record');

// Fonction pour obtenir la phase actuelle selon le temps écoulé
const getCurrentPhase = (timeElapsed) => {
  let elapsed = 0;
  for (let i = 0; i < PHASES_WITH_PAUSES.length; i++) {
    const phase = PHASES_WITH_PAUSES[i];
    if (timeElapsed >= elapsed && timeElapsed < elapsed + phase.duration) {
      return { phase, index: i, timeInPhase: timeElapsed - elapsed };
    }
    elapsed += phase.duration;
  }
  return { phase: PHASES_WITH_PAUSES[PHASES_WITH_PAUSES.length - 1], index: PHASES_WITH_PAUSES.length - 1, timeInPhase: 0 };
};

const STEPS = [
  {
    id: 'thor42',
    title: 'Diagnostic vocal',
    instruction: 'Test vocal en 10 étapes - 4m42s',
    duration: TOTAL_DURATION, // 287 secondes (affichage: 4m42s pour THOR 42)
    audioFile: () => `thor42-diagnostic-${Date.now()}.wav`
  }
];

const VoxTestsFlow = ({ user, onClose, isOverlay = false, isMinimal = false, onRecordingStateChange, onCountdownChange, onLiveFrequencyChange, onDiagnosticProgress, onDiagnosticComplete, onDiagnosticWarningChange, onDiagnosticErrorChange, onDiagnosticLoadingChange, onAnalyzingBetweenStepsChange, startAtStep = null, onStartRecording, onStopRecording, onOpenProfile, onComplete, onError }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [textEmotion] = useState(() => Math.random() < 0.5 ? 'joyeux' : 'triste'); // Choix aléatoire au début
  
  // Nouveaux états pour le système de phases
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  
  // Fonction de sécurité pour éviter l'erreur STEPS[currentStep] undefined
  const getCurrentStepSafely = () => {
    return currentStep < STEPS.length ? STEPS[currentStep] : STEPS[0];
  };
  
  // Calculs intelligents basés sur les phases
  const currentPhaseData = getCurrentPhase(timeElapsed);
  const currentPhase = currentPhaseData.phase;
  const canInteract = hasStarted ? (currentPhase.type === 'pause') : true; // Avant démarrage: libre, Après démarrage: seulement pauses
  const currentStepNumber = Math.floor(currentPhaseIndex / 2) + 1; // 0,2,4... → 1,2,3...
  
  const [currentInstruction, setCurrentInstruction] = useState(STEPS[0].instruction);
  const [mustStartWithE1, setMustStartWithE1] = useState(false);
  const [dailyUploads, setDailyUploads] = useState(0);
  const [recordings, setRecordings] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [showAlreadySubmittedWarning, setShowAlreadySubmittedWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false);
  const [finalAnalyzing, setFinalAnalyzing] = useState(false);
  const [finalDiagnostic, setFinalDiagnostic] = useState(null);
  const [showFinalDiagnostic, setShowFinalDiagnostic] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // État userInfoStep supprimé - plus besoin de cette étape
  const [liveFrequency, setLiveFrequency] = useState(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const frequencyHistoryRef = useRef([]);
  const smoothedFrequencyRef = useRef(0);
  
  // Nouveaux états pour timer et analyse
  const [countdown, setCountdown] = useState(0);
  const [stepAnalysis, setStepAnalysis] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalyzingBetweenSteps, setIsAnalyzingBetweenSteps] = useState(false);
  const [isInverted, setIsInverted] = useState(true); // Défaut inversé
  const timerRef = useRef(null);
  const previousPhaseIndexRef = useRef(0); // Pour éviter les boucles infinies
  const currentRecordingStepRef = useRef(null); // Pour identifier l'étape en cours d'enregistrement
  const lastTransitionTimeRef = useRef(0); // Pour throttling anti-boucle

  const spring = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(20px)' }
  });

  // Fonction d'inversion des couleurs
  const toggleInversion = () => {
    const newInverted = !isInverted;
    setIsInverted(newInverted);
    
    // Appliquer l'inversion à tout le document HTML
    if (newInverted) {
      document.documentElement.classList.add('inverted');
    } else {
      document.documentElement.classList.remove('inverted');
    }
  };

  // ✨ useEffect pour appliquer l'inversion par défaut
  useEffect(() => {
    // Appliquer l'inversion au chargement si isInverted est true
    if (isInverted) {
      document.documentElement.classList.add('inverted');
    }
    
    // Cleanup à la déconnexion du composant
    return () => {
      document.documentElement.classList.remove('inverted');
    };
  }, []);

  // ✨ useEffect pour fermer automatiquement après diagnostic (DOIT être au top level)
  useEffect(() => {
    if (isMinimal && showFinalDiagnostic && finalDiagnostic) {
      const timer = setTimeout(() => {
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isMinimal, showFinalDiagnostic, finalDiagnostic, onClose]);

  // Charger la progression sauvegardée au démarrage
  useEffect(() => {
    const progressKey = `voxtest_progress_${user.uid || user.btId}`;
    const savedProgress = localStorage.getItem(progressKey);
    if (savedProgress) {
      try {
        const { currentStep: savedStep, recordings: savedRecordings } = JSON.parse(savedProgress);
        
        // Valider savedStep pour éviter l'erreur STEPS[currentStep] undefined
        const validatedStep = savedStep >= 0 && savedStep < STEPS.length ? savedStep : 0;
        if (validatedStep !== savedStep) {
          console.warn(`⚠️ currentStep corrigé de ${savedStep} à ${validatedStep}`);
        }
        
        // Convertir automatiquement les anciens formats au lieu de les supprimer
        console.log('📂 Chargement des enregistrements sauvegardés...');
        
        // Restaurer les enregistrements depuis base64 URLs
        const restoredRecordings = {};
        Object.entries(savedRecordings).forEach(([stepId, recordData]) => {
          console.log(`🔄 Restoring ${stepId}:`, {
            type: typeof recordData,
            hasUrl: !!(recordData?.url),
            isBase64: !!(recordData?.isBase64),
            data: recordData
          });
          
          if (typeof recordData === 'object' && recordData.isBase64) {
            restoredRecordings[stepId] = {
              url: recordData.url,           // Base64 data URL (persistent)
              timestamp: recordData.timestamp,
              size: recordData.size,
              isBase64: true,
              blob: null                     // Will be recreated when needed
            };
          } else if (typeof recordData === 'string' && recordData.startsWith('data:')) {
            // Legacy base64 string format
            restoredRecordings[stepId] = {
              url: recordData,               // Base64 data URL
              timestamp: Date.now(),
              size: 0,
              isBase64: true,
              blob: null
            };
          } else {
            // Very old format or invalid - skip to force re-recording
            console.warn(`⚠️ Skipping invalid recording format for ${stepId}:`, recordData);
          }
        });
        
        // Defer state updates to avoid render cycle conflicts
        setTimeout(() => {
          setCurrentStep(validatedStep);
          setRecordings(restoredRecordings);
        }, 0);
      } catch (error) {
        console.error('❌ Erreur lors du chargement de la progression:', error);
        localStorage.removeItem(progressKey);
      }
    }
  }, [user.uid, user.btId]);

  // Sauvegarder la progression à chaque changement (base64 URLs sont persistantes)
  useEffect(() => {
      // Sauver les URLs base64 (persistantes) sans les blobs (trop volumineux)
      const recordingsForStorage = {};
      Object.entries(recordings).forEach(([stepId, recordingData]) => {
        if (typeof recordingData === 'object' && recordingData.url && recordingData.isBase64) {
          recordingsForStorage[stepId] = {
            timestamp: recordingData.timestamp,
            size: recordingData.size,
            uploaded: true
          };
        } else if (typeof recordingData === 'string') {
          recordingsForStorage[stepId] = recordingData; // String directe
        }
      });
      
      const progress = {
        currentStep,
        recordings: recordingsForStorage
      };
      
      // ✅ FIX QUOTAEXCEEDEDERROR: Gestion d'erreur localStorage avec nettoyage automatique
      try {
        localStorage.setItem(`voxtest_progress_${user.uid || user.btId}`, JSON.stringify(progress));
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.warn('⚠️ localStorage plein - Nettoyage automatique...');
          
          // Nettoyer les anciens enregistrements VoxTest (garde seulement les 3 plus récents)
          const keys = Object.keys(localStorage);
          const voxTestKeys = keys.filter(key => key.startsWith('voxtest_'));
          
          if (voxTestKeys.length > 3) {
            // Supprimer les plus anciens (tri par nom qui inclut le timestamp)
            voxTestKeys.sort().slice(0, -3).forEach(key => {
              try {
                localStorage.removeItem(key);
                console.log(`🗑️ Supprimé: ${key}`);
              } catch (e) {
                console.warn('Erreur suppression:', e);
              }
            });
            
            // Réessayer la sauvegarde
            try {
              localStorage.setItem(`voxtest_progress_${user.uid || user.btId}`, JSON.stringify(progress));
              console.log('✅ Sauvegarde réussie après nettoyage');
            } catch (e) {
              console.error('❌ Échec sauvegarde même après nettoyage:', e);
            }
          }
        } else {
          console.error('❌ Erreur localStorage:', error);
        }
      }
  }, [currentStep, recordings, user.uid, user.btId]);


  // Vérifier si l'utilisateur a déjà soumis ses enregistrements
  useEffect(() => {
    const checkPreviousSubmission = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const userFolder = getUserFolderPath(user);
        const storageRef = ref(storage, userFolder);
        const listResult = await listAll(storageRef);
        if (listResult.items.length > 0) {
          setHasAlreadySubmitted(true);
          setShowAlreadySubmittedWarning(true);
          // Notifier App.jsx de l'état du warning
          if (onDiagnosticWarningChange) {
            onDiagnosticWarningChange(true);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la vérification des soumissions précédentes:", error);
      }
    };
    checkPreviousSubmission();
  }, []);

  // Exposer la fonction de restart globalement pour App.jsx
  useEffect(() => {
    const restart = () => {
      setShowAlreadySubmittedWarning(false);
      setCurrentStep(0);
      setRecordings({});
      setIsRecording(false);
      setIsPaused(false);
      setIsSubmitting(false);
      setShowSuccess(false);
      setShowRecap(false);
      setError('');
      if (onDiagnosticWarningChange) {
        onDiagnosticWarningChange(false);
      }
    };
    
    window.restartDiagnostic = restart;
    return () => {
      delete window.restartDiagnostic;
    };
  }, [onDiagnosticWarningChange]);


  // Handle direct navigation from dashboard
  useEffect(() => {
    if (startAtStep !== null && startAtStep >= 0 && startAtStep < STEPS.length) {
      console.log(`🚀 Starting VoxTest at step ${startAtStep + 1}`);
      setCurrentStep(startAtStep);
    }
  }, [startAtStep, user]);

  // 📊 INITIALISER LE COMPTEUR QUOTIDIEN d'uploads Firebase (SKIP pour VoxTests)
  useEffect(() => {
    if (user?.uid || user?.btId) {
      console.log('📊 VoxTests mode: Skip Firebase daily counter');
      setDailyUploads(0);
    }
  }, [user?.uid, user?.btId]);

  const startLiveAnalysis = () => {
    if (!analyserRef.current) return;
    
    const analyze = () => {
      if (!analyserRef.current) return;
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Trouver la fréquence dominante
      let maxValue = 0;
      let maxIndex = 0;
      
      // Ignorer les très basses fréquences (bruit)
      for (let i = 10; i < bufferLength / 2; i++) {
        if (dataArray[i] > maxValue) {
          maxValue = dataArray[i];
          maxIndex = i;
        }
      }
      
      // Calculer la fréquence seulement si l'amplitude est suffisante
      if (maxValue > 30) { // Seuil de bruit
        const frequency = maxIndex * audioContextRef.current.sampleRate / (bufferLength * 2);
        
        // Limiter la fréquence à des valeurs réalistes pour la voix humaine
        const clampedFrequency = Math.max(80, Math.min(frequency, 2000));
        
        // Ajouter à l'historique (garder les 10 dernières valeurs)
        frequencyHistoryRef.current.push(clampedFrequency);
        if (frequencyHistoryRef.current.length > 10) {
          frequencyHistoryRef.current.shift();
        }
        
        // Calculer la moyenne pondérée (plus de poids aux valeurs récentes)
        const weights = [0.05, 0.05, 0.05, 0.05, 0.1, 0.1, 0.15, 0.15, 0.15, 0.15];
        let weightedSum = 0;
        let totalWeight = 0;
        
        frequencyHistoryRef.current.forEach((freq, index) => {
          const weight = weights[index] || 0.1;
          weightedSum += freq * weight;
          totalWeight += weight;
        });
        
        const averageFrequency = weightedSum / totalWeight;
        
        // Lissage exponentiel pour éviter les changements brusques
        const smoothingFactor = 0.2; // Plus petit = plus lisse
        smoothedFrequencyRef.current = smoothedFrequencyRef.current * (1 - smoothingFactor) + averageFrequency * smoothingFactor;
        
        setLiveFrequency(smoothedFrequencyRef.current);
        
        // Transmettre à App.jsx pour l'animation du tore
        if (onLiveFrequencyChange) {
          onLiveFrequencyChange(smoothedFrequencyRef.current);
        }
      } else {
        // Diminuer progressivement la fréquence si pas de son détecté
        smoothedFrequencyRef.current *= 0.95;
        const finalFreq = smoothedFrequencyRef.current > 80 ? smoothedFrequencyRef.current : null;
        setLiveFrequency(finalFreq);
        
        // Transmettre à App.jsx
        if (onLiveFrequencyChange) {
          onLiveFrequencyChange(finalFreq);
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(analyze);
    };
    
    analyze();
  };

  // Fonction pour analyser toutes les étapes séparées
  const analyzeAllRecordings = async () => {
    console.log('🧬 Analyse de toutes les étapes séparées...');
    
    // Pour le moment, utiliser l'ancienne logique sur la première étape en attendant la refonte complète
    const firstStepId = RECORD_PHASES[0].id;
    const firstRecording = recordings[firstStepId];
    
    if (firstRecording && firstRecording.blob) {
      await analyzeRecording(firstRecording.blob);
    } else {
      console.error('❌ Aucun enregistrement trouvé pour la première étape');
      // Fallback - continuer quand même vers la fin
      setTimeout(() => stopRecording(), 1000);
    }
  };

  const analyzeRecording = async (audioBlob) => {
    try {
      console.log('🔬 analyzeRecording called with blob:', {
        size: audioBlob.size,
        type: audioBlob.type
      });
      
      if (audioBlob.size === 0) {
        console.error('❌ AudioBlob is empty!');
        return;
      }
      
      const stepId = getCurrentStepSafely().id;
      
      // Convert blob to base64 for persistent storage
      const base64Data = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(audioBlob);
      });
      
      // Create a persistent URL using base64
      const audioUrl = base64Data;
      
      // 🔥 UPLOAD IMMÉDIAT FIREBASE pour dataset
      const uploadToFirebase = async () => {
        try {
          if (!user?.uid) {
            console.log('❌ Pas d\'utilisateur connecté - skip Firebase');
            return;
          }

          // 🚫 PROTECTION LIMITE QUOTIDIENNE - 20 enregistrements/jour max
          const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
          const dailyCountKey = `firebase_daily_${user.uid}_${today}`;
          const currentCount = parseInt(localStorage.getItem(dailyCountKey) || '0');
          
          if (currentCount >= 20) {
            console.log('🚫 Limite quotidienne atteinte (20/20) - Skip Firebase');
            
            // Notifier l'utilisateur via spacebar hint
            if (onDiagnosticErrorChange) {
              onDiagnosticErrorChange('⚠️ Limite quotidienne atteinte (20 enregistrements/jour)');
              
              // Effacer le message après 5 secondes
              setTimeout(() => {
                onDiagnosticErrorChange('');
              }, 5000);
            }
            return;
          }

          // Trouver le nom du fichier correspondant à l'étape
          const step = STEPS.find(s => s.id === stepId);
          const fileName = step ? (typeof step.audioFile === 'function' ? step.audioFile() : step.audioFile) : `${stepId}-${Date.now()}.wav`;
          
          // Dossier utilisateur dans Firebase Storage
          const userFolder = getUserFolderPath(user);
          const audioRef = ref(storage, `${userFolder}/${fileName}`);
          
          console.log(`🔥 Upload Firebase: ${fileName}...`);
          
          // Upload vers Firebase
          await uploadBytes(audioRef, audioBlob);
          
          console.log(`✅ Firebase: ${fileName} uploadé avec succès`);
          
          // ⬆️ INCRÉMENTER COMPTEUR QUOTIDIEN après upload réussi
          const newCount = currentCount + 1;
          try {
            localStorage.setItem(dailyCountKey, newCount.toString());
            setDailyUploads(newCount);
            console.log(`📊 Firebase uploads aujourd'hui: ${newCount}/20`);
          } catch (e) {
            console.warn('⚠️ Impossible de sauvegarder le compteur quotidien:', e);
          }
          
          // Optionnel : sauvegarder métadonnées de l'étape
          const stepMetadata = {
            stepId,
            fileName,
            timestamp: new Date().toISOString(),
            size: audioBlob.size,
            userId: user.uid,
            stepNumber: currentStep + 1,
            stepName: step?.title || stepId
          };
          
          const metadataRef = ref(storage, `${userFolder}/${stepId}_metadata.json`);
          const metadataBlob = new Blob([JSON.stringify(stepMetadata, null, 2)], { type: 'application/json' });
          await uploadBytes(metadataRef, metadataBlob);
          
          console.log(`📝 Firebase: Métadonnées ${stepId} sauvées`);
          
          // 🆕 NOUVEAU: Sauvegarder une référence pour downloadAudioZip
          const voxTestRecord = {
            btId: user.btId || user.uid,
            fileName: fileName,
            stepId: stepId,
            timestamp: new Date().toISOString(),
            firebaseFolder: userFolder,
            firebasePath: `${userFolder}/${fileName}`,
            uploaded: true
          };
          
          // Sauvegarder dans localStorage pour récupération par VoxTestsAdmin
          const voxTestKey = `voxtest_uploaded_${user.btId || user.uid}_${stepId}`;
          try {
            localStorage.setItem(voxTestKey, JSON.stringify(voxTestRecord));
            console.log(`💾 VoxTest record sauvé: ${voxTestKey}`);
          } catch (e) {
            console.warn('⚠️ Impossible de sauvegarder le record VoxTest:', e);
          }
          
        } catch (error) {
          console.error('❌ Erreur upload Firebase:', error);
          // Continue quand même - ne pas bloquer l'analyse IA
        }
      };

      // Lancer l'upload Firebase en parallèle (non-bloquant)
      uploadToFirebase();
      
      // Defer state updates to avoid render cycle conflicts
      setTimeout(() => {
        setRecordings(prev => ({
          ...prev,
          [stepId]: {
            url: audioUrl,              // Base64 data URL (persistent)
            blob: audioBlob,            // CONSERVER LE BLOB ORIGINAL
            timestamp: Date.now(),      // Pour cleanup
            size: audioBlob.size,       // Debug info
            isBase64: true,             // Flag to identify base64 URLs
            firebaseUploaded: true      // Flag Firebase upload
          }
        }));
        setIsRecording(false);
      }, 0);
      
      // 🔥 COOLDOWN + ANALYSE INDIVIDUELLE PROGRESSIVE
      if (isMinimal) {
        // Activer le cooldown immédiatement
        setIsAnalyzingBetweenSteps(true);
        
        // Notifier App.jsx du cooldown
        if (onAnalyzingBetweenStepsChange) {
          onAnalyzingBetweenStepsChange(true);
        }
        
        // Map des équations THOR 42 - NOUVEAU SYSTÈME
        const equationMessages = {
          'thor42': { equation: 'THOR42', archetype: '∆Φ-Complet' },
          // Anciens IDs pour compatibilité (au cas où)
          'identification': { equation: 'E1', archetype: '∆Φ-Racine' },
          'emotion-reading': { equation: 'E2', archetype: '∆Φ-Sacré' },
          'memory-dream': { equation: 'E3', archetype: '∆Φ-Plexus' },
          'vocalization': { equation: 'E4', archetype: '∆Φ-Cœur' },
          'conscious-breathing': { equation: 'E5', archetype: '∆Φ-Gorge' },
          'emotional-reading': { equation: 'E6', archetype: '∆Φ-Frontal' },
          'phoneme-repetition': { equation: 'E7', archetype: '∆Φ-Couronne' },
          'emotional-game': { equation: 'E8', archetype: '∆Φ-Éthérique' },
          'body-scan': { equation: 'E9', archetype: '∆Φ-Astral' },
          'cross-voice': { equation: 'E10', archetype: '∆Φ-Mental' },
          'free-voice': { equation: 'E11', archetype: '∆Φ-Causal' }
        };
        
        const equationInfo = equationMessages[stepId];
        
        // 🔍 DEBUG : Vérifier la correspondance stepId
        console.log('🔍 DEBUG stepId:', stepId, 'equationInfo:', equationInfo);
        
        // Lancer l'analyse IA individuelle
        const analysisPromise = (async () => {
          try {
            // Analyse locale client-side (remplace le backend)
            const analysis = await analyzeAudioLocally(audioBlob);
            
            console.log('🔍 DEBUG analysis complet du backend:', analysis);
            
            const stepData = {
              // 🔥 GARDER TOUTE LA STRUCTURE pour ne rien perdre
              ...analysis,  // Tout l'objet analysis
              features: analysis.features || {},  // Référence explicite aux features
              timestamp: new Date().toISOString()
            };
            
            setStepAnalysis(prev => ({
              ...prev,
              [stepId]: stepData
            }));
            
            // Remplir le tore progressivement
            if (onDiagnosticProgress) {
              // 🎯 CORRECTION : Gérer le cas où equationInfo n'existe pas
              let equationIndex = 0; // Pour THOR42, on utilise toutes les équations d'un coup
              if (equationInfo && equationInfo.equation) {
                if (equationInfo.equation === 'THOR42') {
                  equationIndex = 0; // THOR42 remplit toutes les équations
                } else {
                  equationIndex = parseInt(equationInfo.equation.substring(1)) - 1; // E1 → 0, E2 → 1, etc.
                }
              } else {
                console.log('⚠️ equationInfo manquant, utilisation fallback index:', equationIndex);
              }
              
              // Normalisation score 0-1
              let normalizedScore = 0.7;
              if (analysis.globalScore) {
                normalizedScore = Math.max(0, Math.min(1, analysis.globalScore / 100));
              } else if (analysis.features?.thor_health_score) {
                const rawScore = analysis.features.thor_health_score;
                normalizedScore = rawScore > 1 ? rawScore / 100 : rawScore;
                normalizedScore = Math.max(0, Math.min(1, normalizedScore));
              }
              
              console.log(`✅ ${equationInfo?.equation || 'Analyse locale'} completee`);
              
              console.log('🔍 VoxTestsFlow sending to onDiagnosticProgress:', {
                equationIndex,
                hasEquationResults: !!analysis.equation_results,
                equationResultsKeys: analysis.equation_results ? Object.keys(analysis.equation_results).length : 0,
                hasFeatures: !!analysis.features,
                hasPersonalized: !!analysis.personalized
              });
              
              onDiagnosticProgress(equationIndex, {
                message: analysis.personalized?.recommendation || `${equationInfo?.equation || 'Analyse'} terminee`,
                score: normalizedScore,
                archetype: analysis.personalized?.frequency_archetype || equationInfo?.archetype || '',
                emotion: analysis.personalized?.emotional_patterns || 'Neutre',
                equation_results: analysis.equation_results,
                features: analysis.features,
                personalized: analysis.personalized,
                metrics_42: analysis.metrics_42,
                globalScore: analysis.globalScore
              });
            }
            
            return analysis;
          } catch (error) {
            console.error("Erreur analyse IA:", error);
            return null;
          }
        })();
        
        // Attendre minimum 3s + fin analyse
        Promise.all([
          analysisPromise,
          new Promise(resolve => setTimeout(resolve, 3000))
        ]).then(() => {
          // Désactiver le cooldown
          setIsAnalyzingBetweenSteps(false);
          
          // Notifier App.jsx
          if (onAnalyzingBetweenStepsChange) {
            onAnalyzingBetweenStepsChange(false);
          }
          
          
          // Passer à l'étape suivante ou terminer
          if (currentStep < STEPS.length - 1) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
          } else {
            // Diagnostic THOR 42 complet - 42 équations analysées
            console.log('🎉 Diagnostic THOR 42 complet - 42 équations analysées !');
            
            // Forcer la fermeture du micro
            if (isRecording) {
              stopRecording();
            }
            
            // Notifier App.jsx que le diagnostic est complet
            if (onDiagnosticComplete) {
              // 🔥 UTILISER LES DONNÉES DE stepAnalysis car analysis n'est pas dans ce scope
              const thorData = stepAnalysis['thor42'] || {};
              
              console.log('🔍 DEBUG thorData depuis stepAnalysis:', thorData);
              console.log('🔍 DEBUG stepAnalysis complet:', stepAnalysis);
              
              const enrichedDiagnostic = {
                completed: true,
                stepAnalysis: stepAnalysis,
                // 🎯 EXTRAIRE LES VRAIES DONNÉES depuis stepAnalysis
                features: thorData.features || thorData || {},
                metrics: thorData.features || thorData || {},  // Les métriques sont dans features
                thor_health_score: thorData.thor_health_score || thorData.personalized?.thor42_analysis?.thor_health_score,
                ml_prediction: thorData.ml_prediction || thorData.personalized?.ml_prediction,
                thor_analysis: thorData.personalized?.thor42_analysis || thorData.thor42_analysis || {},
                equation_results: thorData.equation_results || {},
                // Données brutes complètes pour debug
                raw_analysis: thorData
              };
              
              console.log('🎯 VoxTestsFlow envoi diagnostic COMPLET:', enrichedDiagnostic);
              onDiagnosticComplete(enrichedDiagnostic);
            }
            
            // Afficher le message final avec lien profil
            setTimeout(() => {
              if (onDiagnosticErrorChange) {
                onDiagnosticErrorChange('✨ Diagnostic terminé ! Allez au [profil] pour voir les résultats');
              }
            }, 500); // Plus rapide pour voir le message
            
            // ✅ FIX ÉCRAN NOIR: Transition vers l'écran de succès
            setTimeout(() => {
              setShowSuccess(true); // Afficher l'écran de succès
              if (onDiagnosticLoadingChange) {
                onDiagnosticLoadingChange(false); // Arrêter le loading
              }
              if (onDiagnosticErrorChange) {
                onDiagnosticErrorChange(''); // Effacer le message pour laisser place au succès
              }
            }, 2000); // Laisser temps de voir le message puis passer au succès
          }
        });
      }
      
      // Mode non-minimal (ancienne logique conservée)
      if (!isMinimal) {
        console.log('🧬 VoxTest Starting personalized AI analysis for step:', stepId, 'User:', user.btId || user.uid);
        setIsAnalyzing(true);
        try {
          const analysis = await aiService.analyzeSingleRecording(audioBlob, user.btId || user.uid);
          const stepData = {
            ...analysis.features,
            personalized: analysis.personalized,
            equation_results: analysis.equation_results,  // 🔥 NOUVEAU: Équations réelles
            timestamp: new Date().toISOString()
          };
          
          setStepAnalysis(prev => ({
            ...prev,
            [stepId]: stepData
          }));
          
          // 🎯 REMPLIR LE TORE PROGRESSIVEMENT !
          if (onDiagnosticProgress) {
            const stepIndex = currentStep; // currentStep est 0-indexé (0-10)
            onDiagnosticProgress(stepIndex, {
              message: analysis.personalized?.recommendation || `Étape ${currentStep} analysée`,
              score: analysis.features?.thor_health_score || Math.random() * 100,
              archetype: analysis.personalized?.frequency_archetype || 'Non déterminé',
              emotion: analysis.personalized?.emotional_patterns || 'Neutre'
            });
          }
        } catch (error) {
          console.error("Erreur analyse IA:", error);
          // Afficher un message d'erreur user-friendly
          setStepAnalysis(prev => ({
            ...prev,
            [stepId]: {
              error: true,
              message: "L'analyse n'a pas pu traiter cet enregistrement. Vous pouvez continuer.",
              timestamp: new Date().toISOString()
            }
          }));
        } finally {
          setIsAnalyzing(false);
        }
      }
      
    } catch (error) {
      console.error("Erreur lors de l'analyse de l'enregistrement:", error);
      setError("Erreur lors de l'analyse de l'enregistrement");
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange("Erreur lors de l'analyse de l'enregistrement");
      }
    }
  };

  // 🔥 NOUVELLE FONCTION UPLOAD FIREBASE CONTINU
  const uploadToFirebase = async (audioBlob, stepId) => {
    try {
      if (!user?.uid) {
        console.log('❌ Pas d\'utilisateur connecté - skip Firebase');
        return;
      }

      // 🚫 PROTECTION LIMITE QUOTIDIENNE - 20 enregistrements/jour max
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const dailyCountKey = `firebase_daily_${user.uid}_${today}`;
      const currentCount = parseInt(localStorage.getItem(dailyCountKey) || '0');
      
      if (currentCount >= 20) {
        console.log('🚫 Limite quotidienne atteinte (20/20) - Skip Firebase');
        
        // Notifier l'utilisateur via spacebar hint
        if (onDiagnosticErrorChange) {
          onDiagnosticErrorChange('⚠️ Limite quotidienne atteinte (20 enregistrements/jour)');
          
          // Effacer le message après 5 secondes
          setTimeout(() => {
            onDiagnosticErrorChange('');
          }, 5000);
        }
        return;
      }

      // Nom de fichier simple avec extension WAV (convention existante)
      const fileName = `${stepId}.wav`; // Format simple : etape1.wav, etape2.wav...
      
      // Dossier utilisateur dans Firebase Storage
      const userFolder = getUserFolderPath(user);
      const audioRef = ref(storage, `${userFolder}/${fileName}`);
      
      console.log(`🔥 Upload Firebase: ${fileName}...`);
      
      // Upload vers Firebase
      await uploadBytes(audioRef, audioBlob);
      
      console.log(`✅ Firebase: ${fileName} uploadé avec succès`);
      
      // ⬆️ INCRÉMENTER COMPTEUR QUOTIDIEN après upload réussi
      const newCount = currentCount + 1;
      try {
        localStorage.setItem(dailyCountKey, newCount.toString());
        setDailyUploads(newCount);
        console.log(`📊 Firebase uploads aujourd'hui: ${newCount}/20`);
      } catch (e) {
        console.warn('⚠️ Impossible de sauvegarder le compteur quotidien:', e);
      }
      
      // Note: localStorage VoxTest supprimé car inutile (admin utilise Firebase directement)
      
    } catch (error) {
      console.error('❌ Erreur upload Firebase:', error);
      // Continue quand même - ne pas bloquer le processus
    }
  };

  const initializeAudio = async () => {
    try {
      if (!window.AudioContext && !window.webkitAudioContext) {
        throw new Error("Votre navigateur ne supporte pas l'API AudioContext");
      }

      // 🔍 S'assurer qu'on part toujours d'un état propre
      if (audioContextRef.current) {
        console.log("🧹 Fermeture de l'ancien AudioContext avant d'en créer un nouveau");
        try {
          await audioContextRef.current.close();
        } catch (e) {
          console.warn("Erreur fermeture ancien AudioContext:", e);
        }
        audioContextRef.current = null;
      }

      // Créer un NOUVEAU AudioContext propre à chaque fois
      console.log("🆕 Création d'un AudioContext frais pour le diagnostic");
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      analyserRef.current.smoothingTimeConstant = 0.8;
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;

      console.log("✅ Audio context et analyser initialisés avec succès - État:", audioContextRef.current.state);
      return true;
    } catch (error) {
      console.error("Erreur lors de l'initialisation audio:", error);
      const errorMsg = "Impossible d'initialiser l'audio. Veuillez vérifier votre navigateur.";
      setError(errorMsg);
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(errorMsg);
      }
      return false;
    }
  };

  // Fonction pour convertir AudioBuffer en WAV blob (stéréo)
  const bufferToWave = async (audioBuffer, length) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;

    // ✅ INTERLEAVED data (L-R-L-R...) pour compatibilité lecteurs audio
    const data = new Float32Array(length * numberOfChannels);
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        data[i * numberOfChannels + channel] = audioBuffer.getChannelData(channel)[i];
      }
    }

    const dataLength = data.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);

    // Convert float samples to PCM - Volume maximum pour qualité diagnostique
    const volume = 1.0; // Maximum volume sans distortion
    let offset = 44;
    for (let i = 0; i < data.length; i++) {
      const sample = Math.max(-1, Math.min(1, data[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF * volume, true);
      offset += 2;
    }

    return new Blob([buffer], { type: 'audio/wav' });
  };

  const startRecording = async () => {
    try {
      setError(null);
      // Marquer que le diagnostic a commencé - verrouillage activé
      setHasStarted(true);
      
      // Nettoyer l'erreur dans App.jsx aussi
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange('');
      }
      
      // 🧹 NETTOYAGE PRÉVENTIF : S'assurer qu'aucun stream ne traîne
      if (mediaStreamRef.current) {
        console.log("🧹 NETTOYAGE: Fermeture d'un ancien stream qui traînait");
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log("🔴 Ancien track fermé:", track.kind);
        });
        mediaStreamRef.current = null;
      }
      
      setIsRecording(true);
      setIsPaused(false);
      
      // Notifier App.jsx de l'état d'enregistrement
      if (onRecordingStateChange) {
        onRecordingStateChange(true);
      }

      const audioInitialized = await initializeAudio();
      if (!audioInitialized) {
        setIsRecording(false);
        return;
      }

      // Créer le MediaStream UNE SEULE FOIS ici
      // Configuration optimale pour diagnostic médical vocal
      const audioConstraints = {
        echoCancellation: false,        // Pas d'annulation d'écho (on veut le vrai son)
        noiseSuppression: false,        // Pas de suppression de bruit (préserver toutes les fréquences)
        autoGainControl: true,          // ✅ ACTIVÉ pour volume fort sur iPhone
        sampleRate: { ideal: 48000 },  // Haute qualité d'échantillonnage
        channelCount: 1                 // ✅ MONO pur - converti en stéréo après
      };

      // Sur iOS, essayer de s��lectionner le meilleur micro (arrière pour iPhone Pro)
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        // Essayer d'obtenir le micro arrière (meilleure qualité sur iPhone Pro)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const backMic = devices.find(device =>
            device.kind === 'audioinput' &&
            (device.label.includes('back') || device.label.includes('arrière'))
          );
          if (backMic) {
            audioConstraints.deviceId = { exact: backMic.deviceId };
            console.log('🎙️ Micro arrière iPhone détecté:', backMic.label);
          }
        } catch (e) {
          console.log('📱 Micro par défaut utilisé');
        }
      }

      const stream = await requestMicrophone(audioConstraints);
      mediaStreamRef.current = stream;
      console.log("📡 MediaStream créé:", stream.getTracks().length, "tracks");
      console.log("🎚️ Config audio:", stream.getAudioTracks()[0].getSettings());

      // Connecter le stream à l'analyseur
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Configuration MediaRecorder pour qualité diagnostique maximale
      let recorderOptions = {
        audioBitsPerSecond: 256000  // Haute qualité (256 kbps)
      };

      // Utiliser le meilleur codec disponible
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        recorderOptions.mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        recorderOptions.mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        recorderOptions.mimeType = 'audio/wav';
      }

      console.log('🎙️ Codec utilisé:', recorderOptions.mimeType, '@', recorderOptions.audioBitsPerSecond, 'bps');

      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('🎤 Chunks collected:', audioChunksRef.current.length);
        let audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('🎵 AudioBlob created (raw):', {
          size: audioBlob.size,
          type: audioBlob.type
        });

        // 🔧 CONVERSION MONO → STÉRÉO pour compatibilité lecture
        try {
          const arrayBuffer = await audioBlob.arrayBuffer();
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

          console.log('🎚️ Audio source:', {
            channels: audioBuffer.numberOfChannels,
            sampleRate: audioBuffer.sampleRate,
            duration: audioBuffer.duration
          });

          // Si mono, créer un buffer stéréo en dupliquant le canal
          if (audioBuffer.numberOfChannels === 1) {
            console.log('🔄 Conversion MONO → STÉRÉO...');
            const stereoBuffer = audioContext.createBuffer(
              2, // 2 canaux (stéréo)
              audioBuffer.length,
              audioBuffer.sampleRate
            );

            const monoData = audioBuffer.getChannelData(0);
            // Dupliquer le canal mono sur gauche ET droite
            stereoBuffer.getChannelData(0).set(monoData); // Canal gauche
            stereoBuffer.getChannelData(1).set(monoData); // Canal droit

            // Convertir le buffer stéréo en WAV blob
            const wavBlob = await bufferToWave(stereoBuffer, audioBuffer.length);
            audioBlob = wavBlob;
            console.log('✅ Conversion terminée - Stéréo WAV:', wavBlob.size, 'bytes');
          }

          audioContext.close();
        } catch (error) {
          console.warn('⚠️ Conversion stéréo échouée, utilisation audio original:', error);
        }

        // Utiliser l'ID de l'étape stockée au démarrage de l'enregistrement
        const stepId = currentRecordingStepRef.current;
        
        console.log('🔍 DEBUG onstop - currentRecordingStepRef.current:', stepId);
        
        if (stepId) {
          console.log(`💾 Sauvegarde étape ${stepId}`);
          
          // Sauvegarder l'étape individuelle
          setRecordings(prev => ({
            ...prev,
            [stepId]: {
              url: URL.createObjectURL(audioBlob),
              blob: audioBlob,
              timestamp: Date.now(),
              size: audioBlob.size,
              stepId: stepId
            }
          }));
          
          // 🔥 UPLOAD FIREBASE IMMÉDIAT (Option A)
          await uploadToFirebase(audioBlob, stepId);
          
          // Réinitialiser la ref
          currentRecordingStepRef.current = null;
        } else {
          console.warn('⚠️ Aucun stepId trouvé dans currentRecordingStepRef');
        }
        
        // Si c'est la dernière étape (etape10), lancer l'analyse finale
        if (stepId === 'etape10') {
          console.log('✅ Toutes les étapes terminées - Analyse complète');
          setTimeout(async () => {
            await analyzeAllRecordings();
          }, 100);
        }
      };

      audioChunksRef.current = [];
      // Stocker l'ID de l'étape qui commence (etape1)
      currentRecordingStepRef.current = 'etape1';
      console.log('🔍 DEBUG startRecording - currentRecordingStepRef set to:', currentRecordingStepRef.current);
      
      // Initialiser previousPhaseIndexRef à 0 (première phase = etape1)
      previousPhaseIndexRef.current = 0;
      lastTransitionTimeRef.current = Date.now();
      console.log('🔍 DEBUG startRecording - previousPhaseIndexRef initialized to:', previousPhaseIndexRef.current);
      
      mediaRecorderRef.current.start();
      startLiveAnalysis();
      
      // Démarrer le timer avec arrêt automatique
      const totalDuration = TOTAL_DURATION; // 287 secondes
      setCountdown(totalDuration);
      
      // Notifier immédiatement App.jsx du countdown initial
      if (onCountdownChange) {
        onCountdownChange(totalDuration);
      }
      
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          const newValue = prev <= 1 ? 0 : prev - 1;
          const elapsed = totalDuration - newValue;
          
          // Mettre à jour timeElapsed pour les calculs de phase
          setTimeElapsed(elapsed);
          
          // Obtenir la phase actuelle et mettre à jour l'instruction
          const phaseData = getCurrentPhase(elapsed);
          const previousPhaseIndex = previousPhaseIndexRef.current;
          
          setCurrentInstruction(phaseData.phase.instruction);
          setCurrentPhaseIndex(phaseData.index);
          
          // Gestion des transitions entre phases pour 10 fichiers séparés - AVEC PROTECTION ANTI-RÉPÉTITION RENFORCÉE
          const now = Date.now();
          const timeSinceLastTransition = now - lastTransitionTimeRef.current;
          
          if (phaseData.index !== previousPhaseIndex && mediaRecorderRef.current && timeSinceLastTransition > 500) {
            console.log('🔍 DEBUG transition - previousPhaseIndex:', previousPhaseIndex, '→ phaseData.index:', phaseData.index, 'timeSinceLastTransition:', timeSinceLastTransition);
            
            // Mettre à jour les refs immédiatement pour éviter les répétitions
            previousPhaseIndexRef.current = phaseData.index;
            lastTransitionTimeRef.current = now;
            
            const previousPhase = PHASES_WITH_PAUSES[previousPhaseIndex];
            const currentPhase = phaseData.phase;
            
            console.log('🔍 DEBUG phases - previous:', previousPhase?.type, previousPhase?.id, '→ current:', currentPhase?.type, currentPhase?.id);
            
            // Fin d'une étape d'enregistrement → Stop et sauvegarde
            if (previousPhase?.type === 'record' && currentPhase.type === 'pause') {
              console.log(`🛑 Fin étape ${previousPhase.id} → Stop MediaRecorder`);
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
              }
            }
            
            // Début d'une étape d'enregistrement après pause → Start nouveau 
            else if (previousPhase?.type === 'pause' && currentPhase.type === 'record') {
              console.log(`🎤 Début étape ${currentPhase.id} → Start nouveau MediaRecorder`);
              // Reset chunks pour nouveau fichier
              audioChunksRef.current = [];
              // Stocker l'ID de l'étape qui commence
              currentRecordingStepRef.current = currentPhase.id;
              console.log('🔍 DEBUG transition start - currentRecordingStepRef set to:', currentRecordingStepRef.current);
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
                mediaRecorderRef.current.start();
              }
            }
          }
          
          // Notifier App.jsx du countdown (defer to avoid render cycle)
          if (onCountdownChange) {
            setTimeout(() => onCountdownChange(newValue), 0);
          }
          
          if (newValue <= 0) {
            clearInterval(timerRef.current);
            
            // 🔒 FORCER FERMETURE MICRO IMMÉDIATE AVANT TOUT
            if (mediaStreamRef.current) {
              console.log("🔒 FERMETURE FORCÉE IMMÉDIATE du micro");
              mediaStreamRef.current.getTracks().forEach(track => {
                track.stop();
                console.log("🔴 Track fermée immédiatement:", track.kind);
              });
            }
            
            // ⏳ DÉLAI AVANT stopRecording pour laisser le temps au micro de se fermer
            setTimeout(() => {
              console.log("⏳ Timer expiré - Délai écoulé, arrêt final");
              stopRecording();
            }, 500);
          }
          
          return newValue;
        });
      }, 1000);

    } catch (error) {
      console.error("Erreur lors du démarrage de l'enregistrement:", error);
      const friendly = getFriendlyMicError(error);
      setError(friendly);
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(friendly);
      }
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      console.log("🛑 ARRÊT SIMPLE et IMMÉDIAT");
      
      // 1. FERMER LE MICRO EN PREMIER (priorité absolue)
      if (mediaStreamRef.current) {
        console.log("🔒 FERMETURE FORCÉE des tracks");
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log("🔴 Track fermée:", track.kind, track.readyState);
        });
        mediaStreamRef.current = null;
      }
      
      // 2. Arrêter le timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // 3. Réinitialiser les états de phase pour éviter désynchronisation
      setTimeElapsed(0);
      setCurrentPhaseIndex(0);
      setCountdown(0);
      setHasStarted(false); // Permettre de redémarrer
      
      // 4. Arrêter l'animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // 4. Arrêter MediaRecorder (normal) 
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.log("🎬 Arrêt MediaRecorder");
        mediaRecorderRef.current.stop();
        
        // ⏳ ATTENDRE que le micro soit vraiment fermé avant que l'IA démarre
        console.log("⏳ Attente pour fermeture complète du micro...");
      }

      // 5. Fermer AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log("🔊 Fermeture AudioContext");
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // 6. États
      setIsRecording(false);
      setLiveFrequency(null);
      
      // 7. Notifier App.jsx
      if (onRecordingStateChange) {
        onRecordingStateChange(false);
      }
      if (onCountdownChange) {
        onCountdownChange(0);
      }
      if (onLiveFrequencyChange) {
        onLiveFrequencyChange(null);
      }

      console.log("✅ MICRO FERMÉ - Arrêt terminé");

    } catch (error) {
      console.error("Erreur lors de l'arrêt:", error);
      setIsRecording(false);
    }
  };


  // Exposer les fonctions d'enregistrement pour App.jsx (bouton micro)
  useEffect(() => {
    if (onStartRecording) {
      onStartRecording(startRecording);
    }
    if (onStopRecording) {
      onStopRecording(stopRecording);
    }
  }, [onStartRecording, onStopRecording]);

  // Hook global pour barre d'espace - aligné sur AuthenticatedApp + verrouillage phases
  const canRecord = !showConfirmExit && !showRecap && !showSuccess && !showFinalDiagnostic && !isAnalyzingBetweenSteps && canInteract;
  useSpacebarRecording(isRecording, canRecord, startRecording, stopRecording);

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Dernière étape atteinte - proposer de voir le diagnostic final
      onClose('show-ai-diagnostic');
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleBack = () => {
    if (isRecording) {
      setError('Veuillez arrêter l\'enregistrement en cours avant de quitter');
      return;
    }
    setShowConfirmExit(true);
  };

  const handleConfirmExit = () => {
    // Nettoyer les ressources audio
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    onClose();
  };

  const handleCancelExit = () => {
    setShowConfirmExit(false);
  };

  // Fonction utilitaire pour slugifier les champs (nom, prénom, email)
  function slugify(str) {
    return String(str)
      .normalize('NFD')
      .replace(/[^\w\s.-@]/g, '')
      .replace(/\s+/g, '_')
      .replace(/@/g, '_at_')
      .replace(/\./g, '_')
      .toLowerCase();
  }


  // Fonction pour uploader les enregistrements vers Firebase
  const submitRecordings = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      // Notifier App.jsx du loading
      if (onDiagnosticLoadingChange) {
        onDiagnosticLoadingChange(true);
      }
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange('');
      }
      
      console.log('🚀 Début de l\'upload des enregistrements...');
      
      // Préparer les informations utilisateur
      const userInfo = {
        nom: user.displayName?.split(' ')[1] || 'Non renseigné',
        prenom: user.displayName?.split(' ')[0] || 'Non renseigné', 
        email: user.email || 'Non renseigné',
        timestamp: new Date().toISOString()
      };
      
      // Dossier utilisateur dans Firebase Storage
      const userFolder = `recordings/${user.uid}`;
      
      // Convertir chaque enregistrement et l'uploader
      const uploadPromises = Object.entries(recordings).map(async ([stepId, recordingData]) => {
        try {
          // Debug info pour diagnostiquer le problème
          console.log(`🔍 Processing ${stepId}:`, {
            hasBlob: !!recordingData.blob,
            hasUrl: !!recordingData.url,
            blobSize: recordingData.blob?.size,
            dataType: typeof recordingData
          });
          
          // Récupérer le blob depuis l'enregistrement ou recréer depuis base64
          let audioBlob = recordingData.blob;
          
          if (!audioBlob && recordingData.url && recordingData.isBase64) {
            // Convertir base64 data URL vers blob
            const response = await fetch(recordingData.url);
            audioBlob = await response.blob();
            console.log(`🔄 Blob récupéré depuis base64 pour ${stepId}: ${audioBlob.size} bytes`);
          }
          
          if (!audioBlob) {
            // Fallback: essayer de récupérer depuis l'URL (ancien format)
            const url = recordingData.url || recordingData;
            if (typeof url === 'string') {
              audioBlob = await fetch(url).then(r => r.blob());
            }
          }
          
          if (!audioBlob || audioBlob.size === 0) {
            throw new Error(`Blob invalide pour ${stepId}: taille ${audioBlob?.size || 0}`);
          }
          
          // Trouver le nom du fichier correspondant à l'étape
          const step = STEPS.find(s => s.id === stepId);
          const fileName = step ? step.audioFile : `${stepId}.webm`;
          
          // Référence Firebase Storage
          const audioRef = ref(storage, `${userFolder}/${fileName}`);
          
          console.log(`📤 Upload de ${fileName}...`);
          
          // Upload vers Firebase
          await uploadBytes(audioRef, audioBlob);
          
          // Récupérer l'URL de téléchargement
          const downloadURL = await getDownloadURL(audioRef);
          
          console.log(`✅ ${fileName} uploadé avec succès`);
          
          return {
            stepId,
            fileName,
            url: downloadURL,
            size: audioBlob.size
          };
          
        } catch (error) {
          console.error(`❌ Erreur upload ${stepId}:`, error);
          throw new Error(`Erreur lors de l'upload de ${stepId}`);
        }
      });
      
      // Attendre tous les uploads
      const uploadResults = await Promise.all(uploadPromises);
      
      // Créer un fichier métadonnées
      const metadata = {
        userInfo,
        recordings: uploadResults,
        diagnostic: {
          completed_at: new Date().toISOString(),
          total_steps: STEPS.length,
          analysis: stepAnalysis
        }
      };
      
      // Upload des métadonnées
      const metadataRef = ref(storage, `${userFolder}/metadata.json`);
      const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
      await uploadBytes(metadataRef, metadataBlob);
      
      console.log('✅ Tous les enregistrements uploadés avec succès!');
      
      // Lancer l'analyse finale complète avec Docthor
      await performFinalAnalysis();
      
      // Ne pas montrer le succès immédiatement - attendre le diagnostic
      setShowRecap(false);
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      const errorMsg = `Erreur lors de l'envoi: ${error.message}`;
      setError(errorMsg);
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
      // Arrêter le loading dans App.jsx
      if (onDiagnosticLoadingChange) {
        onDiagnosticLoadingChange(false);
      }
    }
  };


  if (showConfirmExit) {
    return (
      <animated.div style={spring} className="voice-recording-flow">
        <div className="step-container">
          <div className="step-header">
            <h2>Confirmation</h2>
          </div>
          <div className="instruction-box">
            <p>Voulez-vous vraiment quitter ? Votre progression sera sauvegardée.</p>
            <div className="navigation-buttons">
              <button onClick={handleCancelExit} className="nav-button prev">
                Continuer
              </button>
              <button onClick={handleConfirmExit} className="nav-button next">
                Quitter
              </button>
            </div>
          </div>
        </div>
      </animated.div>
    );
  }

  // Page userInfo supprimée - utilisation directe des données Firebase



  // Plus de return early - on continue normalement même avec warning
  // App.jsx gère maintenant l'affichage du message


  const isLastStep = currentStep === STEPS.length - 1;


  // Fonction pour passer à l'étape suivante
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(Math.min(currentStep + 1, STEPS.length - 1));
    } else {
      // Fin du diagnostic
      onClose();
    }
  };


  // Mode minimal - juste question au-dessus du tore
  if (isMinimal) {
    // ✨ SUPPRESSION TOTALE PAGE FALLBACK - fermeture immédiate en mode minimal ✨

    // Interface de chargement simple
    if (finalAnalyzing) {
      return (
        <div className="diagnostic-minimal">
          <div className="diagnostic-question-minimal">
            <p>🧠 Analyse de vos enregistrements...</p>
            <div className="loading-dots-elegant">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
            <p style={{fontSize: '0.9rem', opacity: 0.8, marginTop: '1rem'}}>
              Analyse IA en cours...
            </p>
          </div>
        </div>
      );
    }

    // Interface de succès
    if (showSuccess) {
      return (
        <div className="diagnostic-minimal">
          <div className="diagnostic-success">
            <h3>✅ Diagnostic terminé !</h3>
            <p>Vos enregistrements ont été envoyés avec succès.</p>
            <button onClick={onClose} className="close-success">Fermer</button>
          </div>
        </div>
      );
    }

    // ✨ POPUP IMMONDE SUPPRIMÉ ! Flux automatique élégant maintenant ✨

    // Interface normale
    return (
      <div className={`diagnostic-minimal ${isInverted ? 'inverted' : ''}`}>
        <div className="diagnostic-question-minimal">
          <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
        </div>
        
        {/* Popup de texte à lire - Étape 6 */}
        <TextToReadPopup 
          show={isRecording && currentPhase.id === 'etape6'}
          emotion={textEmotion}
        />
        
        {/* Bouton inversion en bas à droite */}
        <button 
          className="invert-button"
          onClick={toggleInversion}
          title={isInverted ? 'Mode normal' : 'Mode inversé'}
        >
          ⚪
        </button>
      </div>
    );
  }

  // Mode overlay simple - copie exacte d'AuthenticatedApp
  if (isOverlay) {
    return (
      <div className={`app ${isInverted ? 'inverted' : ''}`}>
        {/* Instructions en haut, au-dessus du tore */}
        <div className="diagnostic-minimal">
          <div className="diagnostic-question-minimal">
            <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
          </div>
        </div>

        <div className="voxtest-torus-container">
          <div
            onClick={() => canInteract && (isRecording ? stopRecording() : startRecording())}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: canInteract ? 'pointer' : 'default'
            }}
          >
            <TorusAnimation
              liveFrequency={liveFrequency}
              isRecording={isRecording}
              isGuidedRecording={isRecording}
              disablePopup={true}
            >
              {/* Spacebar hint en bas avec timer intégré comme AuthenticatedApp */}
              <div className="spacebar-hint">
                <MicToggle
                  isOpen={isRecording}
                  onClick={(e) => {
                    e.stopPropagation();
                    canInteract && (isRecording ? stopRecording() : startRecording());
                  }}
                  style={{
                    opacity: canInteract ? 1 : 0.5,
                    pointerEvents: canInteract ? 'auto' : 'none'
                  }}
                />
                <div className={`mic-indicator ${isRecording ? 'active' : ''}`}></div>
                <span>
                  {!canInteract && isRecording
                    ? (currentPhase.type === 'record' ? "Enregistrement en cours..." : "Pause - Respirez...")
                    : (isRecording
                      ? (/iPhone|iPad|Android/i.test(navigator.userAgent)
                        ? "Touchez le Torus ou le micro pour arrêter"
                        : "Barre d'espace pour arrêter l'enregistrement")
                      : (/iPhone|iPad|Android/i.test(navigator.userAgent)
                        ? "Touchez le Torus ou le micro pour parler"
                        : "Barre d'espace pour parler"))
                  }
                </span>
                {countdown > 0 && (
                  <span className="diagnostic-timer">
                    {currentPhase.type === 'record'
                      ? `Étape ${currentStepNumber}/10 - ${currentPhaseData.timeInPhase ? Math.ceil(currentPhase.duration - currentPhaseData.timeInPhase) : currentPhase.duration}s`
                      : `Pause - Respirez (${currentPhaseData.timeInPhase ? Math.ceil(currentPhase.duration - currentPhaseData.timeInPhase) : currentPhase.duration}s)`
                    }
                  </span>
                )}
              </div>
            </TorusAnimation>
          </div>
        </div>
        
        {/* Popup de texte à lire - Étape 6 */}
        <TextToReadPopup 
          show={isRecording && currentPhase.id === 'etape6'}
          emotion={textEmotion}
        />
        
        {/* Bouton inversion en bas à droite */}
        <button 
          className="invert-button"
          onClick={toggleInversion}
          title={isInverted ? 'Mode normal' : 'Mode inversé'}
        >
          ⚪
        </button>
      </div>
    );
  }

  // Mode page complète (version originale)
  return (
    <animated.div style={spring} className={`voice-recording-flow ${isInverted ? 'inverted' : ''}`}>
      <div className="step-container">
        <div className="step-header">
          <button onClick={handleBack} className="back-button">
            ← Retour
          </button>
          <h2>{getCurrentStepSafely().title}</h2>
          <p className="duration">Durée suggérée : {getCurrentStepSafely().duration}s</p>
        </div>

        <div className="instruction-box">
          <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
          <div className="spacebar-hint">
            <div className={`mic-indicator ${isRecording ? 'active' : ''}`}></div>
            <span>
              {/iPhone|iPad|Android/i.test(navigator.userAgent)
                ? "Touchez le Torus ou le micro pour parler"
                : "Barre d'espace pour parler"}
            </span>
          </div>
          
          {/* Affichage de l'analyse en temps réel */}
          {isAnalyzing && (
            <div className="analysis-loading">
              <p>🩺 Analyse de vos fréquences vocales...</p>
              <div className="loading-dots">
                <span>•</span><span>•</span><span>•</span>
              </div>
            </div>
          )}
          
          {stepAnalysis[getCurrentStepSafely().id] && (
            <div className="step-analysis-feedback">
              {stepAnalysis[getCurrentStepSafely().id].error ? (
                <div className="analysis-error">
                  <h4>🤔 Analyse</h4>
                  <p>{stepAnalysis[getCurrentStepSafely().id].message}</p>
                </div>
              ) : (
                <>
                  <h4>📊 Analyse vocale</h4>
                  <div className="analysis-metrics">
                    {stepAnalysis[getCurrentStepSafely().id].f0_mean && (
                      <div className="metric">
                        <span className="label">Fréquence fondamentale:</span>
                        <span className="value">{Math.round(stepAnalysis[getCurrentStepSafely().id].f0_mean)} Hz</span>
                      </div>
                    )}
                    {stepAnalysis[getCurrentStepSafely().id].energy_mean !== undefined && (
                      <div className="metric">
                        <span className="label">Énergie vocale:</span>
                        <span className="value">{Math.round(stepAnalysis[getCurrentStepSafely().id].energy_mean * 100)}%</span>
                      </div>
                    )}
                    {stepAnalysis[getCurrentStepSafely().id].jitter !== undefined && (
                      <div className="metric">
                        <span className="label">Stabilité (jitter):</span>
                        <span className="value">{stepAnalysis[getCurrentStepSafely().id].jitter.toFixed(2)}%</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="recording-controls" style={{
          minHeight: 'max(50vh, 300px)',
          maxHeight: '70vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px'
        }}>
          {/* Timer flottant */}
          {isRecording && countdown > 0 && (
            <div className="floating-timer">
              {currentPhase.type === 'record'
                ? `Étape ${currentStepNumber}/10 - ${currentPhaseData.timeInPhase ? Math.ceil(currentPhase.duration - currentPhaseData.timeInPhase) : currentPhase.duration}s`
                : `Pause - Respirez (${currentPhaseData.timeInPhase ? Math.ceil(currentPhase.duration - currentPhaseData.timeInPhase) : currentPhase.duration}s)`
              }
            </div>
          )}

          {/* Wrapper clickable pour le Torus */}
          <div
            onClick={() => {
              if (!isRecording && !recordings[getCurrentStepSafely().id]?.blob) {
                startRecording();
              } else if (isRecording) {
                stopRecording();
              }
            }}
            style={{
              cursor: 'pointer',
              position: 'relative',
              width: 'min(90vw, 600px)',
              height: 'min(90vw, 600px)',
              maxWidth: '600px',
              maxHeight: '600px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <TorusAnimation
              liveFrequency={liveFrequency}
              isGuidedRecording={isRecording}
              isRecording={isRecording}
            >
              {(!isRecording && !recordings[getCurrentStepSafely().id]?.blob) && (
                <button
                  className="floating-mic-button"
                  onClick={(e) => {
                    e.stopPropagation(); // Éviter double-click avec wrapper
                    startRecording();
                  }}
                  aria-label="Démarrer l'enregistrement"
                >
                  Je parle
                </button>
              )}
              {isRecording && (
                <button
                  className="floating-mic-button recording"
                  onClick={(e) => {
                    e.stopPropagation(); // Éviter double-click avec wrapper
                    stopRecording();
                  }}
                  aria-label="Arrêter l'enregistrement"
                >
                  J'arrête
                </button>
              )}
            </TorusAnimation>
          </div>
        </div>


        {error && (
          <div className="error-message" style={{ color: 'red', marginTop: '1rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {loading && (
          <div className="loading-message" style={{ marginTop: '1rem', textAlign: 'center' }}>
            Envoi en cours...
          </div>
        )}
      </div>
      
      {/* Popup de texte à lire - Étape 6 (95-140s) */}
      <TextToReadPopup 
        show={isRecording && countdown > 0 && (260 - countdown) >= 95 && (260 - countdown) < 140}
      />
      
      {/* Bouton inversion en bas à droite */}
      <button 
        className="invert-button"
        onClick={toggleInversion}
        title={isInverted ? 'Mode normal' : 'Mode inversé'}
      >
        ⚪
      </button>
    </animated.div>
  );
};

export default VoxTestsFlow;
